// Grafik üzerinden yakınlaştırmanın KURALLARI — ApexCharts'tan bağımsız ve saf.
//
// Jest (sürükleme ya da tekerlek) yalnız iki sayı üretiyor; onu kabul
// edilebilir bir pencereye çevirme işi burada. Böylece kural tarayıcı
// kütüphanesi olmadan test edilebiliyor.
//
// Üç kural ölçülerek kondu:
//
//  1. TAM SAYI ms ZORUNLU. Sürükleme kesirli epoch üretiyor (…884.1252) ve
//     ApexCharts tik dizisini `stripNumber(val, 7)` ile kuruyor — 13 haneli
//     bir epoch'ta bu, pencereyi ölçülen değerlerle 100 sn geri / 7.560 sn
//     ileri kaydırıyor. Saniyeye yuvarlamak hem tam sayı veriyor hem de veri
//     çözünürlüğüne (~5 sn örnekleme) oturuyor.
//
//  2. ASLA GENİŞLETMEZ. Sonuç daima kullanıcının seçtiği pencerenin içinde
//     kalır; dışarı taşmak "yakınlaştırma yok" demektir. "24 saat" yazan
//     segment ile 40 saat gösteren grafik aynı ekranda duramaz.
//
//  3. ALT SINIR VAR. 2 saniyelik pencere sunucuda geçerli ama ekranda 0–1
//     bant kalır ve kullanıcı "veri yok" sanar.

/** En dar yakınlaştırma penceresi. */
export const EN_DAR_ZOOM_MS = 60_000

/** Jest bitiminden veri çekimine kadar beklenen süre — tekerlek serisini toplar. */
export const ZOOM_BEKLEME_MS = 250

export type ZoomPenceresi = { bas: number; son: number }

const saniyeAlt = (ms: number) => Math.floor(ms / 1000) * 1000
const saniyeUst = (ms: number) => Math.ceil(ms / 1000) * 1000

/**
 * Jestin istediği aralığı uygulanabilir pencereye çevirir.
 *
 * @param istenen jestin ürettiği ham aralık (kesirli olabilir)
 * @param sinir kullanıcının seçtiği pencere — sonuç bunun dışına çıkamaz
 * @returns `null` ise yakınlaştırma yok demektir (ön ayara dönülür)
 */
export function zoomPenceresi(
  istenen: ZoomPenceresi,
  sinir: ZoomPenceresi,
): ZoomPenceresi | null {
  const tam = { bas: saniyeAlt(sinir.bas), son: saniyeUst(sinir.son) }
  const genislik = tam.son - tam.bas
  if (!Number.isFinite(genislik) || genislik <= 0) return null
  // Ön ayarın kendisi alt sınırdan darsa yakınlaşacak yer yok.
  if (genislik <= EN_DAR_ZOOM_MS) return null

  if (!Number.isFinite(istenen.bas) || !Number.isFinite(istenen.son)) return null

  let bas = saniyeAlt(Math.min(istenen.bas, istenen.son))
  let son = saniyeUst(Math.max(istenen.bas, istenen.son))

  // Alt sınır: merkezden genişlet.
  if (son - bas < EN_DAR_ZOOM_MS) {
    const orta = Math.round((bas + son) / 2)
    bas = saniyeAlt(orta - EN_DAR_ZOOM_MS / 2)
    son = saniyeUst(orta + EN_DAR_ZOOM_MS / 2)
  }

  // Kelepçe: pencere ön ayarın dışına taşamaz; taşarsa kaydırılır.
  if (bas < tam.bas) {
    son += tam.bas - bas
    bas = tam.bas
  }
  if (son > tam.son) {
    bas -= son - tam.son
    son = tam.son
  }
  bas = Math.max(bas, tam.bas)
  son = Math.min(son, tam.son)

  // Tüm pencereyi kapsıyorsa bu bir yakınlaştırma değil, sıfırlamadır.
  if (bas <= tam.bas && son >= tam.son) return null
  return { bas, son }
}

/**
 * Merkezden ölçekleme — tekerlek ve düğmeler bunu kullanıyor.
 * `carpan < 1` yakınlaştırır, `> 1` uzaklaştırır.
 */
export function olcekle(
  suAn: ZoomPenceresi,
  sinir: ZoomPenceresi,
  carpan: number,
): ZoomPenceresi | null {
  const orta = (suAn.bas + suAn.son) / 2
  const yari = ((suAn.son - suAn.bas) * carpan) / 2
  return zoomPenceresi({ bas: orta - yari, son: orta + yari }, sinir)
}
